import React, { useState } from 'react';
import styled from 'styled-components';
import { CheckCircle, Download, Star, Calendar, DollarSign, Eye, RefreshCw, FileText, Award, Filter, Search } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const CompletedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CompletedCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  }
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 20px;
  position: relative;
`;

const CompletedBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const QuoteTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const QuoteId = styled.p`
  opacity: 0.9;
  margin: 0 0 5px 0;
  font-size: 14px;
`;

const QuoteDate = styled.p`
  opacity: 0.8;
  margin: 0;
  font-size: 12px;
`;

const CardContent = styled.div`
  padding: 25px;
`;

const ProjectSummary = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SummaryItem = styled.div``;

const SummaryLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin: 0 0 5px 0;
`;

const SummaryValue = styled.p`
  font-size: 14px;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
`;

const FinalAmount = styled.div`
  background: #dcfce7;
  border: 1px solid #22c55e;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
`;

const AmountTitle = styled.h4`
  font-size: 14px;
  color: #15803d;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const AmountValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #15803d;
  margin-bottom: 5px;
`;

const AmountNote = styled.p`
  font-size: 12px;
  color: #166534;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${props => props.primary ? '#3b82f6' : props.success ? '#10b981' : 'white'};
  color: ${props => (props.primary || props.success) ? 'white' : '#374151'};
  border: ${props => (props.primary || props.success) ? 'none' : '1px solid #d1d5db'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => 
      props.primary ? '#2563eb' : 
      props.success ? '#059669' : '#f3f4f6'};
  }
`;

const RatingSection = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
`;

const RatingTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: #d97706;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StarRating = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 10px;
`;

const StarButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.filled ? '#fbbf24' : '#d1d5db'};
  font-size: 18px;
  
  &:hover {
    color: #fbbf24;
  }
`;

const ReviewText = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  resize: none;
  
  &:focus {
    outline: none;
    border-color: #fbbf24;
  }
`;

const ProjectTimeline = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f3f4f6;
`;

const TimelineTitle = styled.h5`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 10px 0;
`;

const TimelineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 5px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #9ca3af;
`;

function CompletedQuotes() {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [ratings, setRatings] = useState({});
  const [reviews, setReviews] = useState({});

  // 더미 완료된 견적 데이터
  const completedQuotes = [
    {
      id: 'QT-2024-003',
      title: '로고 디자인',
      category: '디자인',
      requestDate: '2024-02-05',
      completedDate: '2024-02-20',
      finalAmount: 180,
      originalBudget: '100-300',
      duration: '15일',
      description: '새로운 브랜드 로고 디자인 작업',
      deliverables: ['로고 메인 버전', '가로형/세로형 변형', '컬러/흑백 버전', '다양한 파일 포맷'],
      satisfaction: null,
      projectManager: '이디자이너',
      timeline: [
        { phase: '요구사항 분석', date: '2024-02-05' },
        { phase: '초안 제작', date: '2024-02-08' },
        { phase: '1차 수정', date: '2024-02-12' },
        { phase: '최종 완성', date: '2024-02-20' }
      ]
    },
    {
      id: 'QT-2023-045',
      title: '쇼핑몰 웹사이트 개발',
      category: '웹개발',
      requestDate: '2023-11-15',
      completedDate: '2024-01-30',
      finalAmount: 850,
      originalBudget: '500-1000',
      duration: '76일',
      description: '반응형 쇼핑몰 웹사이트 개발',
      deliverables: ['반응형 웹사이트', '관리자 페이지', '결제 시스템', '회원 관리', '상품 관리'],
      satisfaction: 4,
      projectManager: '김웹개발',
      timeline: [
        { phase: '기획 및 설계', date: '2023-11-20' },
        { phase: '디자인 작업', date: '2023-12-05' },
        { phase: '개발 진행', date: '2024-01-15' },
        { phase: '테스트 및 배포', date: '2024-01-30' }
      ]
    },
    {
      id: 'QT-2023-038',
      title: '모바일 앱 개발',
      category: '앱개발',
      requestDate: '2023-10-01',
      completedDate: '2023-12-25',
      finalAmount: 1200,
      originalBudget: '1000+',
      duration: '85일',
      description: 'iOS/Android 크로스플랫폼 앱 개발',
      deliverables: ['iOS 앱', 'Android 앱', '관리자 웹패널', 'API 서버', '앱스토어 등록'],
      satisfaction: 5,
      projectManager: '박앱개발',
      timeline: [
        { phase: '요구사항 정의', date: '2023-10-05' },
        { phase: 'UI/UX 디자인', date: '2023-10-20' },
        { phase: '앱 개발', date: '2023-12-10' },
        { phase: '스토어 출시', date: '2023-12-25' }
      ]
    }
  ];

  const filteredQuotes = completedQuotes.filter(quote => {
    const matchesCategory = categoryFilter === 'all' || quote.category === categoryFilter;
    const matchesSearch = searchTerm === '' ||
      quote.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleRating = (quoteId, rating) => {
    setRatings(prev => ({ ...prev, [quoteId]: rating }));
  };

  const handleReviewChange = (quoteId, review) => {
    setReviews(prev => ({ ...prev, [quoteId]: review }));
  };

  const submitReview = (quoteId) => {
    const rating = ratings[quoteId];
    const review = reviews[quoteId];
    
    if (rating) {
      // 실제로는 서버에 리뷰를 전송
      console.log('Submitting review:', { quoteId, rating, review });
      alert('리뷰가 제출되었습니다. 감사합니다!');
    }
  };

  const getBudgetText = (budget) => {
    switch(budget) {
      case '100-300': return '100-300만원';
      case '300-500': return '300-500만원';
      case '500-1000': return '500-1000만원';
      case '1000+': return '1000만원 이상';
      default: return budget;
    }
  };

  return (
    <ContentLayout 
      title="완료된 견적"
      subtitle="완료된 프로젝트들을 확인하고 평가할 수 있습니다"
    >
      <Container>
        <FilterSection>
          <FilterSelect 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">전체 카테고리</option>
            <option value="웹개발">웹개발</option>
            <option value="앱개발">앱개발</option>
            <option value="디자인">디자인</option>
            <option value="마케팅">마케팅</option>
          </FilterSelect>

          <FilterSelect 
            value={periodFilter} 
            onChange={(e) => setPeriodFilter(e.target.value)}
          >
            <option value="all">전체 기간</option>
            <option value="recent">최근 3개월</option>
            <option value="half">최근 6개월</option>
            <option value="year">최근 1년</option>
          </FilterSelect>

          <SearchInput
            type="text"
            placeholder="프로젝트명, 견적번호 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </FilterSection>

        {filteredQuotes.length > 0 ? (
          <CompletedList>
            {filteredQuotes.map(quote => (
              <CompletedCard key={quote.id}>
                <CardHeader>
                  <CompletedBadge>
                    <CheckCircle size={12} />
                    완료
                  </CompletedBadge>
                  <QuoteTitle>{quote.title}</QuoteTitle>
                  <QuoteId>견적번호: {quote.id}</QuoteId>
                  <QuoteDate>완료일: {quote.completedDate}</QuoteDate>
                </CardHeader>

                <CardContent>
                  <ProjectSummary>
                    <SummaryRow>
                      <SummaryItem>
                        <SummaryLabel>카테고리</SummaryLabel>
                        <SummaryValue>{quote.category}</SummaryValue>
                      </SummaryItem>
                      <SummaryItem>
                        <SummaryLabel>진행 기간</SummaryLabel>
                        <SummaryValue>{quote.duration}</SummaryValue>
                      </SummaryItem>
                      <SummaryItem>
                        <SummaryLabel>담당자</SummaryLabel>
                        <SummaryValue>{quote.projectManager}</SummaryValue>
                      </SummaryItem>
                      <SummaryItem>
                        <SummaryLabel>요청 예산</SummaryLabel>
                        <SummaryValue>{getBudgetText(quote.originalBudget)}</SummaryValue>
                      </SummaryItem>
                    </SummaryRow>
                  </ProjectSummary>

                  <FinalAmount>
                    <AmountTitle>최종 계약 금액</AmountTitle>
                    <AmountValue>{quote.finalAmount.toLocaleString()}만원</AmountValue>
                    <AmountNote>부가세 별도</AmountNote>
                  </FinalAmount>

                  <div style={{marginBottom: '15px'}}>
                    <h5 style={{fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 10px 0'}}>
                      주요 납품물
                    </h5>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                      {quote.deliverables.map((item, index) => (
                        <span key={index} style={{
                          background: '#f3f4f6',
                          color: '#374151',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  <ProjectTimeline>
                    <TimelineTitle>프로젝트 타임라인</TimelineTitle>
                    {quote.timeline.map((item, index) => (
                      <TimelineItem key={index}>
                        <span>{item.phase}</span>
                        <span>{item.date}</span>
                      </TimelineItem>
                    ))}
                  </ProjectTimeline>

                  {quote.satisfaction === null ? (
                    <RatingSection>
                      <RatingTitle>
                        <Star size={14} />
                        프로젝트 만족도 평가
                      </RatingTitle>
                      <StarRating>
                        {[1, 2, 3, 4, 5].map(star => (
                          <StarButton
                            key={star}
                            filled={ratings[quote.id] >= star}
                            onClick={() => handleRating(quote.id, star)}
                          >
                            ★
                          </StarButton>
                        ))}
                      </StarRating>
                      <ReviewText
                        placeholder="프로젝트에 대한 후기를 남겨주세요..."
                        value={reviews[quote.id] || ''}
                        onChange={(e) => handleReviewChange(quote.id, e.target.value)}
                      />
                      <div style={{marginTop: '10px', textAlign: 'right'}}>
                        <ActionButton 
                          success
                          onClick={() => submitReview(quote.id)}
                          disabled={!ratings[quote.id]}
                        >
                          평가 제출
                        </ActionButton>
                      </div>
                    </RatingSection>
                  ) : (
                    <RatingSection>
                      <RatingTitle>
                        <Award size={14} />
                        완료된 평가
                      </RatingTitle>
                      <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                        {[1, 2, 3, 4, 5].map(star => (
                          <span key={star} style={{
                            color: star <= quote.satisfaction ? '#fbbf24' : '#d1d5db',
                            fontSize: '16px'
                          }}>
                            ★
                          </span>
                        ))}
                        <span style={{marginLeft: '8px', fontSize: '12px', color: '#6b7280'}}>
                          {quote.satisfaction}/5점
                        </span>
                      </div>
                    </RatingSection>
                  )}

                  <ActionButtons>
                    <ActionButton primary>
                      <Eye size={12} />
                      상세보기
                    </ActionButton>
                    <ActionButton>
                      <Download size={12} />
                      견적서 다운로드
                    </ActionButton>
                    <ActionButton>
                      <RefreshCw size={12} />
                      유사 프로젝트 재의뢰
                    </ActionButton>
                  </ActionButtons>
                </CardContent>
              </CompletedCard>
            ))}
          </CompletedList>
        ) : (
          <EmptyState>
            <EmptyIcon>
              <CheckCircle size={40} />
            </EmptyIcon>
            <h3 style={{margin: '0 0 10px 0', color: '#374151'}}>
              완료된 프로젝트가 없습니다
            </h3>
            <p style={{margin: 0, color: '#6b7280'}}>
              완료된 프로젝트가 있으면 여기에서 확인할 수 있습니다
            </p>
          </EmptyState>
        )}
      </Container>
    </ContentLayout>
  );
}

export default CompletedQuotes;